import { useEffect, useRef, useState } from 'react';

import { IconTimesDefault, IconZoomOutline } from '@iwoca/icons';
import { Input } from '@iwoca/orion';
import { useParams, useSearchParams } from 'react-router';

import { SearchResultsOverlay } from './SearchResultsOverlay';
import { DEFAULT_TAB_PATH, getTabConfigByPath } from '../../config/tabConfig';
import { CustomerRow } from '../../CustomersV2.types';
import { useCustomersSearch } from '../../hooks/useCustomersSearch';
import { trackSearchPerformed } from '../../tracking';
import { DeclinedCustomerModal } from '../DeclinedCustomerModal/DeclinedCustomerModal';

export const CustomersSearch = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [inputValue, setInputValue] = useState(
    () => searchParams.get('search') ?? '',
  );
  const [isOpen, setIsOpen] = useState(false);
  const [declinedRow, setDeclinedRow] = useState<CustomerRow | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // The box sits above the tab outlet, so there is no tab config to read — the
  // route is the only source. Falls back to the default tab for the moment
  // between landing on /pay/customers and the outlet redirecting, so a search
  // fired then still carries the required `tab_name`.
  const { tab: tabParam } = useParams<{ tab: string }>();
  const tab = getTabConfigByPath(tabParam ?? '')?.path ?? DEFAULT_TAB_PATH;

  // One event per search session, not per debounced keystroke: latched on the
  // first term the seller enters and released when the box goes empty, so
  // refining "acme" to "acme trading" stays a single search.
  const hasTrackedTermRef = useRef(false);

  // The table reads `?search=` directly (set by clicking a result row below),
  // not the live input — so emptying the box has to strip it too, or the table
  // stays filtered on a term the box no longer shows. Also resets page, like
  // setFilter/setSort: the unfiltered result set may not have a page 3.
  const clearAppliedSearch = () =>
    setSearchParams((params) => {
      params.delete('search');
      params.delete('page');
      return params;
    });

  // Gate the fetch on the overlay being open so a term seeded from the URL
  // (box shown, overlay closed on landing) doesn't fire a background request.
  const { results, loading, debouncedTerm } = useCustomersSearch(inputValue, {
    enabled: isOpen,
  });
  const showOverlay = isOpen && debouncedTerm.length > 0;

  // Ends the search session, so the next term the seller types counts as a new
  // one. Keyed on the box being emptied rather than on the debounced term:
  // clearing and retyping straight away never lets that value reach empty, so
  // releasing the latch on it would swallow the second search.
  const endSearchSession = () => {
    hasTrackedTermRef.current = false;
  };

  // Fires on the keystroke that starts a search rather than on the debounced
  // term, so no effect has to watch for the debounce settling. Latched, so a
  // whole session — including refining "acme" to "acme trading" — is one event.
  const trackSearchStarted = () => {
    if (hasTrackedTermRef.current) return;
    hasTrackedTermRef.current = true;
    trackSearchPerformed({ tab, event: 'term_entered' });
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div
      ref={containerRef}
      className="relative w-full max-w-[400px] m:max-w-none"
    >
      <Input
        placeholder="Search all customers..."
        iconLeft={<IconZoomOutline />}
        iconRight={
          inputValue ? (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => {
                setInputValue('');
                setIsOpen(false);
                clearAppliedSearch();
                endSearchSession();
              }}
              className="flex cursor-pointer items-center border-none bg-transparent p-0"
            >
              <IconTimesDefault className="h-[24px] w-[24px]" />
            </button>
          ) : undefined
        }
        type="search"
        value={inputValue}
        onFocus={() => setIsOpen(true)}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          const nextValue = event.target.value;
          setInputValue(nextValue);
          setIsOpen(true);
          if (nextValue === '') {
            clearAppliedSearch();
            endSearchSession();
          } else {
            trackSearchStarted();
          }
        }}
        onKeyDown={(event: React.KeyboardEvent) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            setIsOpen(false);
          }
          if (event.key === 'Enter') setIsOpen(true);
        }}
        className="[&_input::-webkit-search-cancel-button]:hidden"
      />
      {showOverlay && (
        <SearchResultsOverlay
          results={results}
          loading={loading}
          term={debouncedTerm}
          onNavigate={() => {
            trackSearchPerformed({ tab, event: 'result_clicked' });
            setIsOpen(false);
          }}
          onDeclinedClick={(row) => {
            trackSearchPerformed({ tab, event: 'declined_opened' });
            setIsOpen(false);
            setDeclinedRow(row);
          }}
        />
      )}
      <DeclinedCustomerModal
        row={declinedRow}
        onClose={() => setDeclinedRow(null)}
      />
    </div>
  );
};
